import axios from "axios";
import { logger } from "helpers/logger";
import { getPreviouslyViewedInventories } from "@Pages/Home/helpers/PreviouslyViewedHelper";
import { getLastViewedListings } from "@Pages/Search/data/helper";

/**
 * Shared, memoized inputs for the two personalised Home rails ("Based on your <city> search"
 * and "Recommended properties for you").
 *
 * On iOS the Home screen resolves the rail region once and hands the same value to both view
 * models. The two rails are independent React components here, so the resolution lives in this
 * module instead and both mount paths await the same promise — one network call, one answer,
 * and the second rail is not starved when the user's only history is a property.
 */

/** Shown when the user has no usable history to recommend from. */
const DEFAULT_RAIL_CANONICAL = "london-1811028205760";

export interface RecentlySeen {
  items: any[];
  failed: boolean;
}

export interface RecentlySeenParams {
  isWhitelabel?: boolean;
  partnerId?: any;
  userCurrency?: string;
}

let recentlySeenPromise: Promise<RecentlySeen> | null = null;
let regionPromise: Promise<string> | null = null;

// Browser only: on the server a module-level memo outlives the request and would hand one
// visitor's history to the next.
const canMemoize = () => typeof window !== "undefined";

/** Per-Home-load memo, not per session. */
export const clearHomeRailRegionCache = () => {
  recentlySeenPromise = null;
  regionPromise = null;
};

/**
 * Drops imageless and inactive inventories, then orders what is left by how recently it was
 * opened. Filters on status, not availability: a sold-out property the user viewed still earns
 * its Recently Seen slot.
 */
const sortRecentlyViewed = (savedRecentListings: string[], result: any[]): any[] => {
  const listings = (result || []).filter(
    (listing: any) =>
      !(
        typeof listing.images === "undefined" ||
        (listing.images && listing.images.length === 0) ||
        listing.status !== "active"
      ),
  );

  return listings
    .filter((item: any) => savedRecentListings?.includes(item?.canonical_name))
    .sort(
      (a: any, b: any) =>
        savedRecentListings.indexOf(a?.canonical_name) -
        savedRecentListings.indexOf(b?.canonical_name),
    );
};

/** The canonical names in `INVENTORIES_LAST_VIEWED`, most recent first. */
export const getRecentlyViewedNames = (): string[] => {
  try {
    return (getLastViewedListings(1, 10) as string[]) || [];
  } catch (err) {
    return [];
  }
};

/**
 * The recently-viewed properties, resolved at most once per Home load.
 *
 * `names` is non-empty when the fetch runs, so an empty result is always either a real
 * filter-out (inactive / imageless) or a failure — never "nothing to ask for". `failed` keeps
 * those apart; every caller still degrades to `[]` so lower tiers can fill the rail.
 */
export const getRecentlySeenCards = ({
  isWhitelabel = false,
  partnerId,
  userCurrency = "",
}: RecentlySeenParams = {}): Promise<RecentlySeen> => {
  if (recentlySeenPromise) return recentlySeenPromise;

  const request = (async () => {
    const names = getRecentlyViewedNames();
    if (!names.length) return { items: [], failed: false };

    try {
      const res: any = await getPreviouslyViewedInventories(
        names,
        isWhitelabel,
        partnerId,
        userCurrency,
      );
      return { items: sortRecentlyViewed(names, res?.result ?? []), failed: false };
    } catch (err) {
      if (axios.isCancel(err)) return { items: [], failed: false };
      logger.log(`[HomeRails] recently-seen fetch FAILED: ${err}`);
      return { items: [], failed: true };
    }
  })();

  if (canMemoize()) recentlySeenPromise = request;
  return request;
};

export interface RailRegionParams extends RecentlySeenParams {
  /** Most recent `recentlyVisited` entry, when there is one. */
  recentSearch?: { name?: string; canonical_name?: string } | null;
  /** Fall back to a top city when there is no history at all. Off for rails that hide instead. */
  useDefaultCity?: boolean;
}

/**
 * The region both rails fill from: the most recent *region* search when there is one, otherwise
 * the parent city of the most recently viewed property.
 *
 * A property search leaves no region behind — properties never enter `recentlyVisited`, they
 * only land in `INVENTORIES_LAST_VIEWED` — so without the fallback a user whose only history is
 * a property gets no region and both rails go empty.
 */
export const resolveRailRegionCanonical = ({
  recentSearch = null,
  useDefaultCity = false,
  ...recentlySeenParams
}: RailRegionParams = {}): Promise<string> => {
  if (recentSearch?.canonical_name?.trim()) {
    return Promise.resolve(recentSearch.canonical_name.trim());
  }

  const resolveRegion =
    regionPromise ??
    (async () => {
      const { items } = await getRecentlySeenCards(recentlySeenParams);
      const fromViewedProperty =
        items.find((item: any) => item?.region_parent_city_canonical)
          ?.region_parent_city_canonical ?? "";
      logger.log(`[HomeRails] region fromViewedProperty=${fromViewedProperty || "nil"}`);
      return fromViewedProperty;
    })();

  if (canMemoize()) regionPromise = resolveRegion;

  return resolveRegion.then((region) => region || (useDefaultCity ? DEFAULT_RAIL_CANONICAL : ""));
};
